import { OrderBy } from "@common/types";
import CryptoJS from "crypto-js";
import { FilterData, SortData, Column } from '@cloudclevr/components';
import type { RecommendationDto as CloudClevRRecommendationDto, RecommendationsListProps as CloudClevRRecommendationsListProps } from '@cloudclevr/components';
import type { DropdownData } from '@cloudclevr/components';
import { IOption } from '@common/types';

// Extended RecommendationDto with practice field for local use
// Includes practice field that may not yet be in published @cloudclevr/components
export interface RecommendationDto extends CloudClevRRecommendationDto {
  practice?: number | null;
}

// Extended RecommendationsListProps with practiceLabels field for local use
// Includes practiceLabels field that may not yet be in published @cloudclevr/components
export interface RecommendationsListProps extends Omit<CloudClevRRecommendationsListProps, 'recommendations'> {
  recommendations: RecommendationDto[];
  practiceLabels?: Record<number, string>;
}

export type RoadmapStatus = 'Draft' | 'Published' | 'Deleted';

export type RecommendationSeverity = 'Critical' | 'Moderate' | 'Low' | 'Information';

export type RecommendationStatus =
  | 'Open'
  | 'Accepted'
  | 'InProgress'
  | 'Completed'
  | 'Declined'
  | 'Archived';

export type RecommendationType = 'Risk' | 'Outcome';

export type CostPeriod = 'Monthly' | 'Quarterly' | 'Annually';

export const GUID_EMPTY = "00000000-0000-0000-0000-000000000000";

export const DECLINE_REASON_CUSTOMER_DECLINED = "The customer declined it";
export interface AttachmentDto {
  id: string;
  blobId?: string;  
  fileName: string;
  mimeType: string;
  fileSize: number;
  contentHash?: string;
  uploadedAt: string; // ISO string
  uploadedByName: string;
  isDeleted: boolean;
  tempId?: string; // For frontend use only
}

export interface CostDto {
  id: string;
  name: string;
  oneOff: number;
  numberOfUnits: number;
  nonRecurringQuantity: number;
  unit: number;
  period: CostPeriod;
  notes: string; 
  isCustomerVisible: boolean;
  templateId?: string | null;
}

export type DropdownCategory = "DELETE_ROADMAP_DROPDOWN" | "DECLINE_RECOMMENDATION" | "REMOVE_RECOMMENDATION" | "PRACTICE-DROPDOWN";

export interface SubCategoryDto {
  id: string;
  name: string;
  severity: number; // Keep as number to match API response
  // Updated field names to match API response
  openRecommendationCount?: number;
  acceptedRecommendationCount?: number;
  inProgressCount?: number;
  completedCount?: number;
  declinedCount?: number;
}

export interface CategoryDto {
  id: string;
  name: string;
  subCategories: SubCategoryDto[];
  templateId?: string | null;
  severity: number; // Keep as number to match API response
  // Updated field names to match API response
  openRecommendationCount?: number;
  acceptedRecommendationCount?: number;
  inProgressCount?: number;
  completedCount?: number;
  declinedCount?: number;
}

export interface JournalDto {
  id: string;
  userId: string;
  createdAt: string;
  activity: string;
  data: string;
  recommendationId?: string | null;
  costId?: string | null;
  roadmapId?: string | null;
}

// Updated to match C# DTOs
export interface RoadmapDto {
  id: string;
  name: string;
  status: number; // Enum value from backend (0 = Draft, etc.)
  customerId: string;
  organizationId: string;
  categories: CategoryDto[];
  recommendations: RecommendationDto[];
  templateId?: string | null;
  openRecommendationCount: number; // Changed from openRecommendations
  acceptedRecommendationCount: number; // Changed from acceptedRecommendations
  severity: number; // Enum value from backend (10=Critical, 20=Moderate, 30=Low, 40=Information)
  createdDate: string; // DateOnly from backend
  modifiedDate: string; // DateOnly from backend
  journals: JournalDto[] | null;
  publishDate?: Date | null; // Optional publish date
}

// Enhanced version with customer information
export interface EnhancedRoadmapDto extends RoadmapDto {
  customerName: string;
  primaryContactEmailAddress: string;
  primaryContactName?: string;
  accountOwnerImageUrl?: string;
  accountOwnerName?: string;  
  accountOwnerEmailAddress?: string;  
  isFavourite: boolean;
}

// For backward compatibility and UI display
export interface Roadmap {
  id: string;
  name: string;
  status: string; // Enum value from backend (0 = Draft, etc.)
  customerId: string;
  organizationId: string;
  openRecommendationCount: number; 
  acceptedRecommendationCount: number; 
  categories: CategoryDto[];
  recommendations: RecommendationDto[];
  templateId?: string | null;
  severity: RecommendationSeverity;
  // Decorated customer info
  customerName?: string;
  customerContact?: string;
  creationDate?: Date | null;
  updatedDate?: Date | null;
  publishDate?: Date | null; // Optional publish date
  roadmapType?: string;
  isFavourite?: boolean;
}

// Types for UI components
export interface RiskProfile {
  level: 'Critical' | 'Moderate' | 'Low' | 'Information';
}

export interface AccountOwner {
  name: string;
  email: string;
  avatar?: string;
}

export interface CategoryOverview {
  id: string;
  name: string;
  openRecommendations: number;
  acceptedRecommendations: number;
  riskLevel: 'Critical' | 'Moderate' | 'Low' | 'Information';
  subcategories?: Array<{
    name: string;
    riskLevel: 'Critical' | 'Moderate'| 'Low' | 'Information';
  }>;
}

export const SEVERITY_OPTIONS = [
  { value: 'Critical', label: 'Critical' },
  { value: 'Moderate', label: 'Moderate' },
  { value: 'Low', label: 'Low' },
  { value: 'Information', label: 'Information' }
];

export type RoadmapResponse = {
  totalItemCount: number;
  itemCount: number;
  totalPageCount: number;
  pageSize: number;
  currentPage: number;
  items: Roadmap[];  
};
export interface UpdateRecommendationRequestDto {
  name?: string | null;
  description: string;
  severity: number;
  status: number;
  type: number;
  title: string;
  notes: string;
  reccomendation: string;  // Note: Backend typo
  quoteUrl: string;
  categoryId: string;
  subCategoryId: string;
  attachments?: AttachmentDto[];
}

export type RoadmapTableColumn = {
  roadmapId: string;
  name: string; // Changed from roadmapType
  customerName: string;
  emailAddress: string;        
  recommendationCount: string; // Combined field for display
  creationDate?: Date | null;
  updatedDate?: Date | null;
  status: string;
  tableDropdown?: string;
  isFavourite?: boolean;  
};

export const ROADMAP_TABLE_COLUMNS: Column<RoadmapTableColumn>[] = [
  { key: 'customerName', displayName: 'Customer name', isComplexCell: true },   
  { key: 'recommendationCount', displayName: 'Recommendations', isComplexCell: true },
  { key: 'creationDate', displayName: 'Creation date', isComplexCell: true },
  { key: 'updatedDate', displayName: '', isComplexCell: true },
  { key: 'status', displayName: 'Status', isComplexCell: true },
  { key: 'tableDropdown', displayName: '' }
];

export const ROADMAP_TABLE_COLUMNS_PROFILE: Column<RoadmapTableColumn>[] = [  
  { key: 'name', displayName: 'Roadmap type', isComplexCell: false },
  { key: 'recommendationCount', displayName: 'Recommendations', isComplexCell: true },
  { key: 'creationDate', displayName: 'Creation date', isComplexCell: true },
  { key: 'updatedDate', displayName: '', isComplexCell: true },
  { key: 'status', displayName: 'Status', isComplexCell: true },
  { key: 'tableDropdown', displayName: '' }
];

// Helper functions to convert between API response and UI types
export const convertSeverityFromNumber = (severity: number): RecommendationSeverity => {
  switch (severity) {
    case 10: return 'Critical';
    case 20: return 'Moderate';
    case 30: return 'Low';
    case 40: return 'Information';
    default: return 'Information';
  }
};

export const convertTypeFromNumber = (type: number): 'Risk' | 'Outcome' => {
  return type === 2 ? 'Outcome' : 'Risk';
};

export const convertSeverityToRiskLevel = (severity: number): 'Critical' | 'Moderate' | 'Low' | 'Information' => {
  switch (severity) {
    case 10: return 'Critical';
    case 20: return 'Moderate';
    case 30: return 'Low';
    case 40: return 'Information';
    default: return 'Information';
  }
};

export const convertStatusFromNumberRoadmap = (status: number): RoadmapStatus => {
  switch (status) {
    case 0: return 'Draft';
    case 1: return 'Published';
    case 2: return 'Deleted';
    default: return 'Draft';
  }
};
// Convert backend status number to frontend RecommendationStatus
export const convertStatusFromNumberRecommendation = (status: number): RecommendationStatus => {
  switch (status) {
    case 0: return 'Open';
    case 10: return 'Accepted';
    case 20: return 'InProgress';
    case 30: return 'Completed';
    case 40: return 'Declined';
    case 90: return 'Archived';
    default: return 'Open';
  }
};

// Convert frontend RecommendationStatus to backend number
export const convertStatusToNumberRecommendation = (status: RecommendationStatus): number => {
  switch (status) {
    case 'Open': return 0;
    case 'Accepted': return 10;
    case 'InProgress': return 20;
    case 'Completed': return 30;
    case 'Declined': return 40;
    case 'Archived': return 90;
    default: return 0;
  }
};

export const convertEnhancedRoadmapToRoadmap = (enhancedRoadmap: EnhancedRoadmapDto): Roadmap => {
  return {
    id: enhancedRoadmap.id,
    name: enhancedRoadmap.name,
    status: convertStatusFromNumberRoadmap(enhancedRoadmap.status),
    customerId: enhancedRoadmap.customerId,
    organizationId: enhancedRoadmap.organizationId,
    openRecommendationCount: enhancedRoadmap.openRecommendationCount,
    acceptedRecommendationCount: enhancedRoadmap.acceptedRecommendationCount,
    categories: enhancedRoadmap.categories,
    recommendations: enhancedRoadmap.recommendations,
    templateId: enhancedRoadmap.templateId,
    severity: convertSeverityFromNumber(enhancedRoadmap.severity),
    customerName: enhancedRoadmap.customerName,
    customerContact: enhancedRoadmap.primaryContactEmailAddress,
    creationDate: new Date(enhancedRoadmap.createdDate),
    updatedDate: new Date(enhancedRoadmap.modifiedDate),
    publishDate: enhancedRoadmap.publishDate ? new Date(enhancedRoadmap.publishDate) : null,
    isFavourite: enhancedRoadmap.isFavourite || false,
    roadmapType: enhancedRoadmap.name // Using name as roadmap type    
  };
};

// Convert API data to UI component props
export const convertToRiskProfile = (severity: number): RiskProfile => {
  return {
    level: convertSeverityToRiskLevel(severity)
  };
};

export const convertToAccountOwner = (roadmap: EnhancedRoadmapDto): AccountOwner => {  
  return {
    name: roadmap.accountOwnerName || 'Unknown',
    email: roadmap.accountOwnerEmailAddress || 'Unknown',
    avatar: roadmap.accountOwnerImageUrl || undefined
  };
};

export interface SubCategoryApiResponse {
  id: string;
  name: string;
}

export const convertToCategories = (categories: CategoryDto[]): CategoryOverview[] => {
  return categories.map(category => ({
    id: category.id,
    name: category.name,
    openRecommendations: category.openRecommendationCount || 0, // CHANGED: use openRecommendationCount
    acceptedRecommendations: category.acceptedRecommendationCount || 0, // CHANGED: use acceptedRecommendationCount
    riskLevel: convertSeverityToRiskLevel(category.severity),
    subcategories: category.subCategories.map(sub => ({
      name: sub.name,
      riskLevel: convertSeverityToRiskLevel(sub.severity) as 'Critical' | 'Moderate' | 'Low' | 'Information'
    }))
  }));
};
// Helper functions to convert between frontend and backend formats
export const convertSeverityToNumber = (severity: string): number => {
  switch (severity) {
    case 'Critical': return 10;
    case 'Moderate': return 20;
    case 'Low': return 30;
    case 'Information': return 40;
    default: return 40;
  }
};

export const convertStatusToNumber = (status: string): number => {
  switch (status) {
    case 'Open': return 0;
    case 'Accepted': return 10;
    case 'InProgress': return 20;
    case 'Completed': return 30;
    case 'Declined': return 40;
    case 'Archived': return 90;
    default: return 0;
  }
};

export const convertTypeToNumber = (type: string): number => {
  switch (type) {
    case 'Risk': return 1;
    case 'Outcome': return 2;
    default: return 1;
  }
};
export const convertFormDataToApiRequest = (
  formData: RecommendationFormData
): CreateRecommendationRequestDto => {
  return {
    name: formData.name || null,
    description: formData.description,
    severity: convertSeverityToNumber(formData.severity),
    status: convertStatusToNumberRecommendation(formData.status),
    type: convertTypeToNumber(formData.type),
    title: formData.title,
    notes: formData.notes,
    reccomendation: formData.recommendation,
    quoteUrl: formData.quoteUrl,
    costs: formData.costs?.map(cost => ({
      id: cost.id,
      name: cost.name,
      oneOff: cost.oneOff,      
      numberOfUnits: cost.numberOfUnits,
      nonRecurringQuantity: cost.nonRecurringQuantity, 
      unit: cost.unit,
      period: convertPeriodToNumber(cost.period),
      notes: cost.notes,
      isCustomerVisible: cost.isCustomerVisible,
      templateId: cost.templateId || null
    })) || [],
    attachments: formData.attachments || [],
    categoryId: formData.categoryId,
    subCategoryId: formData.subCategoryId,
    subCategoryName: formData.subCategoryName || undefined
  };
};

// API Request/Response types for recommendations
export interface CreateRecommendationRequestDto {
  name?: string | null;
  description: string;
  severity: number;
  status: number;
  type: number;
  title: string;
  notes: string;
  reccomendation: string;
  quoteUrl: string;
  costs: Array<{
    id?: string;
    name: string;
    oneOff: number;
    unit: number;
    period: 0 | 1 | 2; // 0 = Monthly, 1 = Quarterly, 2 = Annually
    notes: string;
    isCustomerVisible: boolean;
    templateId?: string | null;
  }>;
  attachments: AttachmentDto[];
  categoryId: string;
  subCategoryId: string;
  subCategoryName?: string;
}

// Frontend form data structure (before conversion to API format)
export interface RecommendationFormData {
  name?: string;
  description: string;
  severity: 'Critical' | 'Moderate' | 'Low' | 'Information';
  status: 'Open' | 'Accepted' | 'InProgress' | 'Completed' | 'Declined' | 'Archived';
  type: 'Risk' | 'Outcome';
  title: string;
  notes: string;
  recommendation: string;
  quoteUrl: string;
  costs: CostDto[];
  attachments: AttachmentDto[];
  categoryId: string;
  subCategoryId: string;
  subCategoryName?: string;
}
// Convert RecommendationDto to form data for auto-filling
export const convertRecommendationDtoToFormData = (recommendation: RecommendationDto): Partial<RecommendationFormData> => {
  return {
    name: recommendation.name || recommendation.title,
    title: recommendation.title,
    severity: typeof recommendation.severity === 'number' 
      ? convertSeverityFromNumber(recommendation.severity) 
      : recommendation.severity, // Handle both number and string severity
    description: recommendation.description,
    recommendation: recommendation.reccomendation, // Note: backend typo
    notes: recommendation.notes,
    quoteUrl: recommendation.quoteUrl || '',
    // Don't set categoryId and subCategoryId as they're already set
  };
};
export interface CreateSubCategoryRequestDto {
  categoryId: string;
  name: string;
}

export interface CreateSubCategoryResponseDto {
  id: string;
  name: string;
}
// Convert RecommendationDto to display format for UI components
export const convertRecommendationDtoToDisplayFormat = (recommendation: RecommendationDto): {
  id: string;
  title: string;
  subcategory: string;
  riskLevel: 'Critical' | 'Moderate' | 'Low' | 'Information';
  riskDescription: string;
  recommendationDescription: string;
  status: RecommendationStatus;
  hasNotes: boolean;
  hasAttachments: boolean;
  attachmentCount: number;
  hasQuote: boolean;
  statusDate?: string;
  reason?: string | null;
} => {
  return {
    id: recommendation.id,
    title: recommendation.title || recommendation.name || 'Untitled',
    subcategory: recommendation.subCategory?.name || 'General',
    riskLevel: convertSeverityFromNumber(
      typeof recommendation.severity === 'string'
        ? parseInt(recommendation.severity, 10)
        : recommendation.severity
    ),
    riskDescription: recommendation.description || 'No risk description available',
    recommendationDescription: recommendation.reccomendation || 'No recommendation available',
    status: typeof recommendation.status === 'number'
      ? convertStatusFromNumberRecommendation(recommendation.status)
      : (recommendation.status as RecommendationStatus),
    hasNotes: !!(recommendation.notes && recommendation.notes.trim().length > 0),
    hasAttachments: !!(recommendation.attachments && recommendation.attachments.length > 0),
    attachmentCount: recommendation.attachments?.length || 0,
    hasQuote: !!(recommendation.quoteUrl && recommendation.quoteUrl.trim().length > 0),
    statusDate: recommendation.statusDate || undefined,
    reason: recommendation.reason || null
  };
};

export interface DeleteRoadmapRequestBody {
  userId: string;
  reason: string;
}
export const isRichTextEmpty = (content: string): boolean => {
  if (!content || !content.trim()) return true;
  
  // Remove HTML tags and check if there's actual text content
  const textContent = content
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
    .trim();
  
  return textContent === '';
};

export interface AcceptRecommendationRequestBody {
  recommendationId: string;
  userId: string;
}

export interface ReopenRecommendationRequestBody {
  recommendationId: string;
  userId: string;
}

export interface DeclineRecommendationRequestBody {
  recommendationId: string;
  userId: string;
  reason: string;
}

export interface InProgressRecommendationRequestBody {
  recommendationId: string;
  userId: string;
  dateStarted: string; // Format: "YYYY-MM-DD"
}
export interface CompleteRecommendationRequestBody {
  recommendationId: string;
  userId: string;
  dateCompletion: string; // Format: "YYYY-MM-DD"
}
export interface RemoveRecommendationRequestBody {
  recommendationId: string;
  reason: string;
  userId: string;
}
export type CostLine = {
  id: string;
  name: string;
  oneOff: number;
  numberOfUnits: number;
  nonRecurringQuantity: number;
  unit: number;
  period: 0 | 1 | 2; // 0 = Monthly, 1 = Quarterly, 2 = Annually
  notes: string;
  isCustomerVisible: boolean;
  hasCosts: boolean;
  canDelete: boolean;
  templateId?: string | null;
};
// API Request/Response types for cost operations
export interface CreateCostLineRequestDto {
  recommendationId: string;
  name: string;
  oneOff: number;
  numberOfUnits: number;
  nonRecurringQuantity: number;
  unit: number;
  period: 0 | 1 | 2; // 0 = Monthly, 1 = Quarterly, 2 = Annually
  notes: string;
  isCustomerVisible: boolean;  
}

export interface UpdateCostLineRequestDto {
  recommendationId: string;
  id: string;
  name: string;
  oneOff: number;
  numberOfUnits: number;
  nonRecurringQuantity: number;
  unit: number;
  period: 0 | 1 | 2; // 0 = Monthly, 1 = Quarterly, 2 = Annually
  notes: string;
  isCustomerVisible: boolean;
}

export interface CostLineResponseDto {
  id: string;
  name: string;
  oneOff: number;
  numberOfUnits: number;
  nonRecurringQuantity: number;
  unit: number;
  period: 0 | 1 | 2;
  notes: string;
  isCustomerVisible: boolean;
  hasCosts: boolean;
  canDelete: boolean;
}

// Helper functions for period conversion
export const convertPeriodFromNumber = (period: number): 'Monthly' | 'Quarterly' | 'Annually' => {
  switch (period) {
    case 0: return 'Monthly';
    case 1: return 'Quarterly';
    case 2: return 'Annually';
    default: return 'Monthly';
  }
};

export const convertPeriodToNumber = (period: 'Monthly' | 'Quarterly' | 'Annually'): 0 | 1 | 2 => {
  switch (period) {
    case 'Monthly': return 0;
    case 'Quarterly': return 1;
    case 'Annually': return 2;
    default: return 0;
  }
};

// Currency formatting helper
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export interface DeleteCostLineRequestDto {
  userId: string;
  reason: string;
}

export interface SetCostVisibilityRequestDto {
  userId: string;
  isCustomerVisible: boolean;
}

export interface CostsRequestDto {
  roadmapId: string;
  searchTerm: string;
  categories: string[];
}

export interface CostFilterCategory {
  name: string;
  categoryId: string;
}

export interface CostFiltersResponse {
  categories: CostFilterCategory[];
}

// This is duplicated in src/components/roadmaps/roadmapDetails/costs/types.ts because it was not importing properly

export interface CostListItemDto {
  category: string;
  subCategory: string;
  name: string;
  description: string;
  nonRecurringCost: number;
  recurringQuantity: number;
  nonRecurringQuantity: number;
  hasCosts: boolean;
  recurringCost: number;
  recurringCostPeriod: string;
  costRecurringTotal: number;
  costNonRecurringTotal: number;
  viewQuoteLink: string;
  // Optional/unit fields used by CostBreakdownTable and related utilities
  unitPrice?: number;
  numberOfUnits?: number;
  unitPricePeriod?: string;
}

export interface CostBreakdownTableColumn {
  id?: string;
  category: string;
  name: string;
  subCategory: string;
  description: string;
  oneOffCost: string;
  unitPrice: string;
  annualCost: string;
  numberOfUnits: number;
  nonRecurringQuantity: number;  
  hasCosts: boolean;
  viewQuote: string;
  isFirstInCategory?: boolean; // Flag to show/hide category name
  viewQuoteLink?: string;
  unitPricePeriod?: string;
}

// Helper function to format currency
export const formatCostCurrency = (amount: number): string => {
  if (amount === 0) return '-';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Helper function to calculate annual cost
export const calculateAnnualCost = (unitPrice: number, period: string): number => {
  if (unitPrice === 0) return 0;
  if (period?.toLowerCase() === 'monthly') {
    return unitPrice * 12;
  }
  return unitPrice; // Assuming "Per year" or annual
};

// Helper function to format cost display with TBC handling
export const formatCostDisplay = (amount: number, showTBC: boolean = false): { main: string; sub?: string } => {
  if (showTBC) {
    return { main: 'TBC', sub: 'Requires scoping' };
  }
  if (amount === 0) {
    return { main: '-' };
  }
  return { main: formatCostCurrency(amount) };
};
export interface BlobCreatedDto {
  blobId: string;
  sasUrl: string;
}
// Calculate file hash for duplicate detection
export const calculateFileMD5 = async (file: File): Promise<string> => {
  try {
    const buffer = await file.arrayBuffer();
    const wordArray = CryptoJS.lib.WordArray.create(buffer);
    const hash = CryptoJS.MD5(wordArray);
    return CryptoJS.enc.Base64.stringify(hash);
  } catch (error) {
    console.warn('MD5 calculation failed:', error);
    return btoa(`${file.name}-${file.size}-${file.lastModified}`).replace(/[^A-Za-z0-9]/g, '');
  }
};
// Helper function to get all content hashes from existing attachments
export const getExistingContentHashes = (attachments: AttachmentDto[]): Set<string> => {  
  const hashes = new Set(
    attachments
      .filter(att => !att.isDeleted && att.contentHash && att.contentHash.trim() !== '')
      .map(att => att.contentHash as string)
  );  
  return hashes;
};

// Date utility functions
export const formatDateToLocalString = (date: Date): string => {
  // Create date string in local timezone to avoid timezone offset issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDisplayDate = (dateString: string): string => {
  if (!dateString) return '';
  // Parse the date string directly without timezone conversion
  const [year, month, day] = dateString.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
};

export const generateCalendar = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay = new Date(currentYear, currentMonth + 1, 0);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const current = new Date(startDate);
  
  for (let i = 0; i < 42; i++) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return {
    days,
    monthName: firstDay.toLocaleDateString('en-GB', { month: 'short' }),
    year: currentYear,
    currentMonth,
    today: today.getDate(),
    firstDay: firstDay.getDate(),
    lastDay: lastDay.getDate()
  };
};

// Interface for AddCosts component communication handlers
export interface AddCostsHandlers {
  handleSave?: () => Promise<void>;
  handleCancel?: () => void;
  isFormValid?: boolean;
}

// Extend the Window interface specifically for roadmap functionality
declare global {
  interface Window {
    roadmapAddCosts?: AddCostsHandlers;
  }
}

export type RoadmapSortBy = 'Customer' | 'Recommendations' | 'CreatedDate';

export interface RoadmapFilterOptions {
  pageSize?: number;
  pageNumber?: number;
  searchTerm?: string;
  sortBy?: RoadmapSortBy;
  order?: OrderBy;
  statusFilter?: number[];
}

export interface RoadmapStatusFilterDto {
  value: number;
  label: string;
}

export interface RoadmapFiltersResponse {
  statuses: RoadmapStatusFilterDto[];
}


export const SETTINGS_ROADMAPS_SORT_BY: SortData[] = [
  {
    label: 'Customer name',
    value: ['Customer'],
    orderByLabels: ['A-Z', 'Z-A'],
  },
  {
    label: 'Recommendations',
    value: ['openrecommendationcount'],
    orderByLabels: ['Lowest first', 'Highest first' ],
  },
  {
    label: 'Creation date',
    value: ['createddate'],
    orderByLabels: ['Furthest first' , 'Soonest first' ],
  }
];

export interface RoadmapFilterData extends FilterData {
  customContent?: (props: {
    selectedFilters: string[];
    handleCheckboxChange: (option: IOption, filter: RoadmapFilterData) => void;
    onApplyFilters?: () => void;
    item: RoadmapFilterData;
  }) => React.ReactNode;
}

export const QUOTE_URL_PATTERN = /^https:\/\/quotes\.cloudclevr\.com\/[a-zA-Z0-9%&+\-_=?#]+$/;
export const QUOTE_URL_MAX_LENGTH = 50;

export const validateQuoteUrl = (url: string): { isValid: boolean; error: string } => {
  if (!url || url.trim() === '') {
    return { isValid: true, error: '' }; // Empty is valid (optional field)
  }

  const trimmedUrl = url.trim();
  
  // Check URL length first
  if (trimmedUrl.length > QUOTE_URL_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Quote URL must be ${QUOTE_URL_MAX_LENGTH} characters or less`
    };
  }

  // Check pattern
  if (!QUOTE_URL_PATTERN.test(trimmedUrl)) {
    return {
      isValid: false,
      error: 'Please enter a valid quote URL starting with https://quotes.cloudclevr.com/ followed by letters, numbers, and these special characters: % & + - _ = ? #'
    };
  }

  return { isValid: true, error: '' };
};

export const hasCustomerVisibleCosts = (recommendations: RecommendationDto[]): boolean => {  
  return !!recommendations && 
    recommendations.length > 0 &&
    recommendations.some(recommendation => 
      recommendation.costs && 
      recommendation.costs.length > 0 && 
      recommendation.costs.some(cost => cost.isCustomerVisible === true)
    );
};